import type { IItemResponse, ILpcoResponse } from '../types/response';
import { type Flow, QuotaType } from '../types/enums';
import { LicenseTypeNature } from '../../../types';

interface IInvoiceItem {
  bolNo: Nullable<string>;
  commDescription: string;
  countryOfOrigin: string;
  hsCode: string;
  itemTotalPrice: string;
  packing: Nullable<string>;
  quantity: string;
  unit: string;
  unitPrice: string;
  packageNumber: string;
  packageType: string;
  grossMass: string;
  netMass: string;
}

export interface IAttachedDocumentExtractionResponse {
  fileName: string;
  fileUrl: string;
  documentType: DocumentType;
  lpco2LicenseType: {
    licenseTypeId: number;
    licenseType: string;
    quotaType: QuotaType;
    flow: Flow;
  };
  mlDocument: {
    typeCode: string;
    type_code: string;
    referenceNumber: string;
    referenceDate: string;
    exporterManufacturerName: string;
    exporterManufacturerAddress: string;
    documentType: 'INVOICE';
    balanceTotal: Nullable<string>;
    besc: Nullable<string>;
    buyerAddress: string;
    buyerName: string;
    cifValue: Nullable<string>;
    currency: string;
    delivery: string;
    flightNo: Nullable<string>;
    fob: string;
    freight: Nullable<string>;
    freightCurrency: Nullable<string>;
    grossWeight: string;
    insurance: Nullable<string>;
    insuranceCurrency: Nullable<string>;
    invoiceDate: Nullable<string>;
    invoiceNumber: Nullable<string>;
    items: IInvoiceItem[];
    netWeight: string;
    paidAdvance: Nullable<string>;
    paymentTerm: Nullable<string>;
    placeReceipt: Nullable<string>;
    portOfDischarge: string;
    portOfLoading: string;
    shippingMethod: string;
    termsOfDelivery: string;
    total: Nullable<string>;
    totalInvoicePrice: string;
    countryOfExport: string;
  };
}

function convertToNumber(val: string) {
  return Number((val ?? '').replace(',', ''));
}

function setRequestedAmount(item: IInvoiceItem, quotaType: string) {
  if (quotaType === QuotaType.NET_MASS) {
    return convertToNumber(item.netMass);
  }

  if (quotaType === QuotaType.GROSS_MASS) {
    return convertToNumber(item.grossMass);
  }
}

export function convertFilexDataToFormData(data: IAttachedDocumentExtractionResponse): ILpcoResponse {
  const formData: ILpcoResponse = {
    additionalFields: [],
    attachedDocuments: [
      {
        code: data.mlDocument.typeCode,
        fileUrl: data.fileUrl,
        referenceNumber: data.mlDocument.referenceNumber,
        attachmentDate: data.mlDocument.referenceDate,
      },
    ],
    countryOfDestinationCode: data.mlDocument.countryOfExport,
    parentLpcoId: null,
    licenseType: data.lpco2LicenseType.licenseType,
    licenseTypeId: data.lpco2LicenseType.licenseTypeId,
    flow: data.lpco2LicenseType.flow,
    invoiceCurrencyCode: data.mlDocument.currency,
    placeOfUnloadingCode: data.mlDocument.portOfDischarge,
    licenseTypeNature: LicenseTypeNature.LPCO,
    userReferenceNumber: null,
    items: [],
    termsOfDeliveryCode: data.mlDocument.termsOfDelivery,
    placeOfLoadingCode: data.mlDocument.portOfLoading,
    beneficiaries: [],
    fees: [],
    dbOptionAdditionalFields: [],
    transactionRecords: [],
    departmentOfficeCode: '',
    departmentOfficeName: '',
    ogaStatus: null,
    ogaStatusInNationalLang: null,
    customsOfficeCode: null,
    customsOfficeName: null,
    exporterNameAndAddress: `${data.mlDocument.exporterManufacturerName ?? ''} ${
      data.mlDocument.exporterManufacturerAddress ?? ''
    }`,
  } as unknown as ILpcoResponse;

  formData.items = data.mlDocument.items.map(
    (item: IInvoiceItem) =>
      ({
        commodityCode: item.hsCode,
        commercialDescription: item.commDescription ?? '',
        countryOfOriginCode: item.countryOfOrigin ?? '',
        itemInvoiceAmountInForeignCurrency: convertToNumber(item.itemTotalPrice),
        packageNumber: item.packageNumber ?? '',
        packageTypeCode: item.packageType ?? '',
        grossMass: convertToNumber(item.grossMass),
        netMass: convertToNumber(item.netMass),
        quotaType: data.lpco2LicenseType.quotaType ?? '',
        requestedAmount: setRequestedAmount(item, data.lpco2LicenseType.quotaType),
      } as unknown as IItemResponse)
  );

  return formData;
}
